'use client';
import { Popover, Transition } from '@headlessui/react';
import {
    ArrowTopRightOnSquareIcon,
    RocketLaunchIcon,
} from '@heroicons/react/20/solid';
import { Fragment, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor } from '../context/EditorContext/EditorContext';
import { useNotifications } from '../context/NotificationContext/NotificationContext';
import { API } from '../utils/api';

function PublishOutsideClick({ open, close, buttonRef, panelRef }) {
    useEffect(() => {
        if (!open) return;

        const ownerDoc = buttonRef.current?.ownerDocument;
        if (!ownerDoc) return;

        const isInsideRef = (ref, target) => {
            const el = ref.current;
            if (!el || !target) return false;
            try {
                return el === target || el.contains(target);
            } catch {
                return false;
            }
        };

        const handlePointerDown = (event) => {
            const target = event.target;
            if (!target || typeof target !== 'object') return;

            if (isInsideRef(buttonRef, target)) return;
            if (isInsideRef(panelRef, target)) return;

            close();
        };

        const docs = new Set([ownerDoc]);
        ownerDoc.querySelectorAll('iframe').forEach((frame) => {
            try {
                if (frame.contentDocument) docs.add(frame.contentDocument);
            } catch {
                // Ignore cross-origin frames
            }
        });

        docs.forEach((doc) => {
            doc.addEventListener('pointerdown', handlePointerDown, true);
        });

        return () => {
            docs.forEach((doc) => {
                doc.removeEventListener('pointerdown', handlePointerDown, true);
            });
        };
    }, [open, close, buttonRef, panelRef]);

    return null;
}

function extractHostname(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    try {
        if (/^https?:\/\//i.test(raw)) {
            return new URL(raw).hostname;
        }
        return new URL(`https://${raw}`).hostname;
    } catch {
        return raw.replace(/^https?:\/\//i, '').split('/')[0];
    }
}

function normalizeSiteUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';

    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

export function Publish({ buttonClass = '', icon = null } = {}) {
    const editor = useEditor();
    const notifications = useNotifications();
    const { company_id, domain, website } = editor.global.data;
    const [loading, setLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
    const liveDomain =
        extractHostname(domain) ||
        extractHostname(website) ||
        'www.clientsdomain.com';
    const liveUrl = normalizeSiteUrl(domain || website || liveDomain);

    useEffect(() => setMounted(true), []);

    const handlePublish = async (closePanel) => {
        closePanel?.();
        setLoading(true);
        try {
            await API.post(`${company_id}/publish`, { env: 'production' });
            notifications.push({
                title: 'Publishing live…',
                variant: 'success',
                ttl: 45,
                useExactTtl: true,
            });
        } catch (error) {
            notifications.push({
                title: 'Failed to publish',
                variant: 'error',
                error,
            });
        } finally {
            setLoading(false);
        }
    };

    const updatePanelPos = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const panelWidth = 240; // matches w-60
        const left = Math.min(
            Math.max(16, rect.right - panelWidth),
            typeof window !== 'undefined'
                ? window.innerWidth - panelWidth - 16
                : rect.right - panelWidth
        );
        const top = rect.bottom + 16;
        setPanelPos({ top, left });
    };

    useEffect(() => {
        const onResize = () => updatePanelPos();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return (
        <Popover className="relative">
            {({ open, close }) => {
                const portalTarget = buttonRef.current?.ownerDocument?.body;
                return (
                    <>
                        <PublishOutsideClick
                            open={open}
                            close={close}
                            buttonRef={buttonRef}
                            panelRef={panelRef}
                        />

                        {/* 🚀 Rocket Button */}
                        <Popover.Button
                            ref={buttonRef}
                            onClick={() => {
                                if (!open) updatePanelPos();
                            }}
                            onFocus={() => {
                                if (!open) updatePanelPos();
                            }}
                            className={
                                buttonClass ||
                                'rounded-full bg-[#2CE080] p-1.5 text-white hover:bg-[#2CE080] focus:outline-none'
                            }
                            title="Publish"
                        >
                            {loading ? (
                                <svg
                                    className="h-4 w-4 animate-spin text-white"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                    />
                                </svg>
                            ) : (
                                icon || <RocketLaunchIcon className="h-4 w-4" />
                            )}
                        </Popover.Button>

                        {mounted &&
                            portalTarget &&
                            createPortal(
                                <Transition
                                    show={open}
                                    as={Fragment}
                                    enter="transition ease-out duration-150"
                                    enterFrom="opacity-0 translate-y-1"
                                    enterTo="opacity-100 translate-y-0"
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 translate-y-0"
                                    leaveTo="opacity-0 translate-y-1"
                                >
                                    <Popover.Panel
                                        ref={panelRef}
                                        static
                                        className="z-[20000] w-60 rounded-md bg-neutral-800 p-3 shadow-lg ring-1 ring-black/30"
                                        style={{
                                            position: 'fixed',
                                            top: panelPos.top,
                                            left: panelPos.left,
                                        }}
                                    >
                                        <h4 className="mb-1 text-[11px] font-semibold text-neutral-400">
                                            Publish destination
                                        </h4>

                                        <div className="flex flex-col gap-1.5">
                                            {/* Staging (grayed out) */}
                                            <label
                                                htmlFor="publish-staging"
                                                className="flex cursor-not-allowed items-center gap-2 rounded-md px-2 py-1.5 opacity-50"
                                            >
                                                <input
                                                    id="publish-staging"
                                                    type="checkbox"
                                                    disabled
                                                    className="h-3.5 w-3.5 accent-blue-600"
                                                />
                                                <div className="flex flex-col leading-tight">
                                                    <span className="text-[12px] text-neutral-400">
                                                        clientname.nicejob.sites
                                                    </span>
                                                    <span className="text-[11px] text-neutral-500">
                                                        Staging (coming soon)
                                                    </span>
                                                </div>
                                            </label>

                                            {/* Live */}
                                            <label
                                                htmlFor="publish-live"
                                                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-700"
                                            >
                                                <input
                                                    id="publish-live"
                                                    type="checkbox"
                                                    checked
                                                    readOnly
                                                    className="h-3.5 w-3.5 accent-green-600"
                                                />
                                                <div className="flex min-w-0 flex-1 flex-col leading-tight">
                                                    <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-white">
                                                        <span className="truncate">
                                                            {liveDomain}
                                                        </span>
                                                        {liveUrl && (
                                                            <a
                                                                href={liveUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(
                                                                    event
                                                                ) =>
                                                                    event.stopPropagation()
                                                                }
                                                                className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-neutral-300 transition-colors hover:text-[#2CE080] focus:outline-none"
                                                                title="Open live site in a new tab"
                                                                aria-label={`Open ${liveDomain} in a new tab`}
                                                            >
                                                                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                                            </a>
                                                        )}
                                                    </span>
                                                    <span className="text-[11px] text-neutral-400">
                                                        Live site
                                                    </span>
                                                </div>
                                            </label>
                                        </div>

                                        <div className="mt-3 border-t border-neutral-700 pt-2 text-right">
                                            <button
                                                onClick={() =>
                                                    handlePublish(close)
                                                }
                                                disabled={loading}
                                                className="rounded-md bg-[#2CE080] px-3 py-1.5 text-[13px] text-white hover:bg-[#2CE080] disabled:opacity-50"
                                            >
                                                {loading
                                                    ? 'Publishing…'
                                                    : 'Publish'}
                                            </button>
                                        </div>
                                    </Popover.Panel>
                                </Transition>,
                                portalTarget
                            )}
                    </>
                );
            }}
        </Popover>
    );
}
